import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Select } from '@/components/UI/Select/Select';
import {
  GetChildPropertyOptionsResponse,
  Property,
  PropertyOption,
} from '@/api/types/categories.types';
import { Collection } from '@/components/FiltersDrawer/FiltersDrawer.types';
import {
  getChildrenPropertyIdsByPropertyValue,
  groupByPropertyLegacyId,
  isCollectionCleared,
  replacePropertyOptions,
} from '@/components/FiltersDrawer/CollectionSelect.helpers';
import { CollectionSelectDrawer } from '@/components/FiltersDrawer/CollectionSelectDrawer';
import { GET_CHILD_PROPERTY_OPTIONS } from '@/api/queries/categories.queries';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';

type Props = {
  propertyLegacyId: string;
  options: PropertyOption[];
  collection: Collection;
  setCollection: React.Dispatch<React.SetStateAction<Collection>>;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  isFiltersOpen?: boolean;
};

export const CollectionSelect = ({
  propertyLegacyId,
  options,
  collection,
  setCollection,
  allProperties,
  popularOptions,
  isFiltersOpen,
}: Props) => {
  const [childPropertyOptions, setChildPropertyOptions] = useState<
    PropertyOption[]
  >([]);
  const [isDrawerOpen, toggleDrawer] = useToggleOpenState();

  const propertyValue = collection[propertyLegacyId];
  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;
  const mainProperty = allProperties?.find(el => el.options?.length);

  function clearCollection(value: string | undefined) {
    const allChildrenPropertyIds: string[] = [];
    const childrenPropertyIds = getChildrenPropertyIdsByPropertyValue(
      options,
      value
    );

    if (childrenPropertyIds?.length) {
      allChildrenPropertyIds.push(...childrenPropertyIds);
      childrenPropertyIds.forEach(id => {
        const ids = getChildrenPropertyIdsByPropertyValue(
          childPropertyOptions,
          collection[id]
        );

        if (ids?.length) {
          allChildrenPropertyIds.push(...ids);
        }
      });
    }

    setCollection(prev => ({
      ...prev,
      [propertyLegacyId]: undefined,
      ...allChildrenPropertyIds?.reduce(
        (acc, id) => ({ ...acc, [id]: undefined }),
        {}
      ),
    }));
  }

  function onClear() {
    clearCollection(propertyValue);
    setChildPropertyOptions([]);
  }

  const { refetch } = useQuery<GetChildPropertyOptionsResponse>(
    GET_CHILD_PROPERTY_OPTIONS,
    { skip: true }
  );

  async function onOptionClick(value: PropertyOption) {
    const isMainProperyClicked =
      propertyLegacyId === mainProperty?.legacyResourceId;

    if (isMainProperyClicked) {
      setCollection({ [propertyLegacyId]: value.legacyResourceId });
      setChildPropertyOptions([]);
    } else {
      clearCollection(collection[propertyLegacyId]);
      setCollection(prev => ({
        ...prev,
        [propertyLegacyId]: value.legacyResourceId,
      }));
    }

    toggleDrawer();

    for await (const childrenPropertyId of value.childrenPropertyIds) {
      const { data } = await refetch({
        legacyId: childrenPropertyId,
        parentLegacyId: value.legacyResourceId,
      });

      setChildPropertyOptions(prev =>
        replacePropertyOptions(prev, data.property.options, childrenPropertyId)
      );
    }
  }

  const selected = options.find(
    el => el.legacyResourceId.toString() === propertyValue
  );

  const allPropertiesIds =
    allProperties?.map(property => property.legacyResourceId) || [];
  const groupedChildOptions = groupByPropertyLegacyId(
    isCollectionCleared(collection) ? [] : childPropertyOptions
  ).sort(
    // TODO: This is a temporary solution for sorting, it is better to sort childrenPropertyIds on the backend side
    ([{ propertyLegacyId: a }], [{ propertyLegacyId: b }]) =>
      allPropertiesIds.indexOf(a) - allPropertiesIds.indexOf(b)
  );

  async function onOpenFilterDrawer() {
    const prop = allProperties?.filter(
      el => el.parentLegacyId === propertyLegacyId
    );

    if (prop?.length && propertyValue) {
      for await (const childProp of prop) {
        const legacyId = childProp.legacyResourceId;

        const { data } = await refetch({
          legacyId,
          parentLegacyId: propertyValue,
        });

        setChildPropertyOptions(prev =>
          replacePropertyOptions(prev, data.property.options, legacyId)
        );
      }
    }
  }

  useEffect(() => {
    if (isFiltersOpen) {
      onOpenFilterDrawer();
    } else {
      setChildPropertyOptions([]);
    }
  }, [isFiltersOpen, collection]);

  useEffect(() => {
    if (isCollectionCleared(collection)) {
      setChildPropertyOptions([]);
    }
  }, [collection]);

  if (!name || !options.length) return null;

  return (
    <div data-testid={`property-select-${propertyLegacyId}`}>
      <Select
        label={name}
        isOpen={isDrawerOpen}
        toggle={toggleDrawer}
        value={selected?.name ?? ''}
        onClear={onClear}
      >
        <CollectionSelectDrawer
          isOpen={isDrawerOpen}
          toggle={toggleDrawer}
          propertyLegacyId={propertyLegacyId}
          options={options}
          collection={collection}
          setCollection={setCollection}
          allProperties={allProperties}
          popularOptions={popularOptions}
          onItemClick={onOptionClick}
        />
      </Select>

      {groupedChildOptions.map(group => {
        return (
          <CollectionSelect
            key={group[0].propertyLegacyId}
            propertyLegacyId={group[0].propertyLegacyId}
            options={group}
            collection={collection}
            setCollection={setCollection}
            allProperties={allProperties}
            isFiltersOpen={isFiltersOpen}
          />
        );
      })}
    </div>
  );
};
